import type { rolesType } from "types/types";

export const SUPERUSER_ROLES = ["super_admin", "admin"];
export const GLOBAL_ROLES = [...SUPERUSER_ROLES, "rektor", "prorektor"];
export const RECTOR_ROLES = ["rektor", "prorektor"];
export const FACULTY_ROLES = ["faculty_admin", "dekan", "zam_dekan", "kafedra_mudiri"];
export const DIRECTION_ROLES = ["direction_admin"];
/** Every staff role that may open the analytics part of the web panel. */
export const ANALYTICS_ROLES = [
  ...GLOBAL_ROLES,
  ...FACULTY_ROLES,
  ...DIRECTION_ROLES,
  "teacher",
  "student",
];

export function normalizeRoleName(name: string | undefined | null): string {
  return (name ?? "").trim().toLowerCase().replace(/\s+/g, "_");
}

/** All normalized role names from the user (any order). */
export function getRoleNames(
  roles: rolesType[] | { name?: string }[] | undefined | null
): string[] {
  if (!roles?.length) return [];
  return roles.map((r) => normalizeRoleName(r.name as string));
}

/** True if the user has any of the given roles (case-insensitive). */
export function hasRole(
  roles: rolesType[] | { name?: string }[] | undefined | null,
  ...candidates: string[]
): boolean {
  const userRoles = getRoleNames(roles);
  const wanted = candidates.map((c) => normalizeRoleName(c));
  return userRoles.some((r) => wanted.includes(r));
}

type MeForRedirect = {
  roles?: { name?: string }[];
  scopes?: { department_id?: number | null; speciality_id?: number | null }[];
  access?: { restricted?: boolean; faculty_ids?: number[]; speciality_ids?: number[] | null } | null;
};

function facultyIds(me: MeForRedirect | undefined): number[] {
  if (me?.access?.faculty_ids) return me.access.faculty_ids;
  return (me?.scopes ?? [])
    .map((s) => s.department_id)
    .filter((d): d is number => d != null);
}

function directionIds(me: MeForRedirect | undefined): number[] {
  if (me?.access?.speciality_ids) return me.access.speciality_ids;
  return (me?.scopes ?? [])
    .map((s) => s.speciality_id)
    .filter((d): d is number => d != null);
}

/** Landing page of a faculty/direction-scoped user: their own page when there is exactly one. */
function scopedLandingPath(me: MeForRedirect | undefined): string | null {
  const names = getRoleNames(me?.roles);
  if (names.some((r) => DIRECTION_ROLES.includes(r))) {
    const ids = directionIds(me);
    return ids.length === 1 ? `/analytics/directions/${ids[0]}` : "/analytics/directions";
  }
  if (names.some((r) => FACULTY_ROLES.includes(r)) || me?.access?.restricted) {
    const ids = facultyIds(me);
    return ids.length === 1 ? `/analytics/faculty/0/${ids[0]}` : "/analytics/faculty";
  }
  return null;
}

/** Post-login landing path: priority respects overlapping roles via first matching tier. */
export function getRedirectPathAfterLogin(me: MeForRedirect | undefined): string {
  const names = getRoleNames(me?.roles);

  if (names.some((r) => SUPERUSER_ROLES.includes(r))) {
    return "/analytics/students";
  }
  if (names.some((r) => RECTOR_ROLES.includes(r))) {
    return "/analytics/faculty";
  }
  const scoped = scopedLandingPath(me);
  if (scoped) return scoped;
  if (names.includes("teacher") || names.includes("student")) {
    return "/class-schedule";
  }
  return "/analytics/faculty";
}

/**
 * 403 sahifasidan "Orqaga" — faqat `super_admin` uchun `/analytics/students`,
 * qolgan rollar o‘z fakultet/yo‘nalish va boshqalar bo‘yicha (login bilan mos).
 */
export function getForbiddenRedirectPath(
  me: MeForRedirect | undefined
): string {
  if (!me?.roles?.length) {
    return "/login";
  }
  const names = getRoleNames(me.roles);

  if (names.includes("super_admin")) {
    return "/analytics/students";
  }
  const scoped = scopedLandingPath(me);
  if (scoped) return scoped;
  if (names.includes("teacher") || names.includes("student")) {
    return "/class-schedule";
  }
  return "/analytics/faculty";
}
